export type AvailableAppType = "app" | "legacy" | "static";
export type RegistrySource = "local-override";

export interface AvailableApp {
  id: string;
  route: string;
  entry: string;
  version: string;
  // Optional registry classification. Omitted, null, and "app" entries are
  // normal user-facing applications; "legacy" and "static" entries are
  // available to launcher-style surfaces but not shell app-switcher chrome.
  type?: AvailableAppType | null;
  navLabel?: string;
  /** Organization Service type used to resolve this app's organization-scoped access. */
  organization_type?: string;
  organizationId?: string;
  category: string;
  description: string;
  /** Image URL or data URL for the app icon. A currentColor SVG data URL inherits `color` and the sidebar tokens in shell chrome; any other icon renders as authored. */
  icon: string;
  /** Optional normalized `#RRGGBB` application navigation color. Colours the wordmark and any currentColor icon. */
  color?: string;
  // Opt-in shell chrome. The shell renders a minimal, top-bar-free layout by
  // default; an app that wants the platform title bar (`kn-content-topbar`)
  // sets this to `true` in its registry entry. Omitted/`false` means no top bar.
  showTopbar?: boolean;
  // How the shell loads this remote's container. Vite/rspack remotes emit a
  // classic-script container loaded via a `<script>` tag ("global", the
  // default). Angular emits an ES-module container (`export`/`import.meta`)
  // that must be loaded via dynamic `import()` — set "module" for those, or
  // the shell throws `Unexpected token 'export'` / `Cannot use 'import.meta'`.
  // The app generator sets this per stack.
  entryType?: "global" | "module";
  // Present when this entry came from a local registry override. The URL identifies
  // the first successful override probe that supplied the entry.
  registrySource?: RegistrySource;
  registrySourceUrl?: string;
  // Declarative side-nav tree for this app, known ahead of mount. Lets the shell
  // render this app's workspace-accordion section (label, icon, items) even when
  // it isn't the currently-mounted app. Absent means the app hasn't published a
  // nav manifest yet; its section renders with no items until it's mounted at
  // least once, at which point the (still-supported) `nav:register` bus path
  // populates it live for as long as it stays the active app.
  navGroups?: AppNavManifestGroup[];
}

export interface AppNavManifestItem {
  id: string;
  label: string;
  route: string;
  /** Image URL or data URL for the route icon. A currentColor SVG data URL follows the sidebar text tokens and their states; any other icon renders as authored. */
  icon?: string;
}

export interface AppNavManifestGroup {
  id: string;
  label?: string;
  /** Image URL or data URL for the section icon, with the same currentColor behaviour as item icons. */
  icon?: string;
  items: AppNavManifestItem[];
}

// A workspace is a named container for one or more apps. Membership is
// many-to-many — the same app may appear in more than one Workspace — so
// membership lives here, not as a field on AvailableApp. `apps` is the fully
// expanded list (not just ids), so a consumer never needs a second lookup
// against a flat app registry to render a workspace.
export interface Workspace {
  id: string;
  label: string;
  /** Optional image URL or data URL for the workspace icon. */
  icon?: string;
  // Display order in the registry-admin list and the shell's workspace dropdown, lowest
  // first; workspaces sharing a value tie-break alphabetically by label. Missing sorts as 0.
  sortOrder?: number;
  apps: AvailableApp[];
}

/** Shared ordering for every workspace picker or workspace-grouped surface. */
export function compareWorkspaces(left: Workspace, right: Workspace): number {
  return (left.sortOrder ?? 0) - (right.sortOrder ?? 0) || left.label.localeCompare(right.label);
}

export interface NavigationOptions {
  // Omitted keeps the current workspace. A string explicitly selects that
  // workspace. Null deliberately creates a tenant-qualified, workspace-free
  // destination for apps that are not assigned to a workspace.
  workspaceId?: string | null;
}

export const registryChangedEvent = "registry:changed";

export interface RegistryChangedPayload {
  tenantId: string;
  entry?: AvailableApp;
}

export type NotificationLevel = "info" | "success" | "warning" | "error";
export type DialogSize = "sm" | "md" | "lg" | "full";

export interface NotificationPushPayload {
  title: string;
  body: string;
  level: NotificationLevel;
}

export interface DialogHandle<TResult = unknown> {
  close: (result: TResult) => void;
  dismiss: () => void;
}

export interface DialogOutcome<TResult = unknown> {
  result?: TResult;
  dismissed: boolean;
}

export interface DialogRequest<TInput, TResult = unknown> {
  title?: string;
  size?: DialogSize;
  dismissible?: boolean;
  data: TInput;
  mount: (
    el: HTMLElement,
    handle: DialogHandle<TResult>,
    data: TInput
  ) => void | Promise<void>;
  unmount: (el: HTMLElement) => void | Promise<void>;
}

export interface DialogService {
  open<TInput, TResult>(request: DialogRequest<TInput, TResult>): Promise<DialogOutcome<TResult>>;
}

export interface AppMountContext {
  appId: string;
  // The real, currently-effective browser path this app is mounted under (tenant prefix
  // included). Pass this straight to a router's `basename` — do not assume it equals the
  // registry's app-relative `route`, which never includes the tenant segment.
  basePath: string;
  initialPath: string;
  availableApps: ReadonlyArray<AvailableApp>;
  // Every workspace available to the active tenant. Membership is many-to-many,
  // so consumers should inspect Workspace.apps rather than expect a workspace id
  // on AvailableApp.
  availableWorkspaces: ReadonlyArray<Workspace>;
  // Resolves an app-relative route to the shell-owned browser URL. Use this for
  // real link hrefs so open-in-new-tab and copy-link preserve tenant/workspace context.
  href: (to: string, options?: NavigationOptions) => string;
  navigate: (to: string, options?: NavigationOptions) => void;
  identity: { userId: string; name: string; email: string; token: () => Promise<string> };
  tenant: { id: string; name: string; features: Record<string, boolean>; token: () => Promise<string> };
  // The workspace this app is currently mounted under, fully expanded — not just an id —
  // so an app can read details about its sibling apps (`workspace.apps`, which includes
  // itself) without a second lookup against `availableApps`, the unscoped tenant-wide list.
  // Null when the app isn't in any workspace (e.g. a preview session, which resolves no
  // workspace context) or the tenant has none configured.
  workspace: Workspace | null;
  /** Non-secret configuration values for the deployed environment. */
  variables: Readonly<Record<string, string>>;
  registryApiBaseUrl?: string;
  bus: PlatformBus;
  dialogs: DialogService;
  logger: Pick<Console, "info" | "warn" | "error">;
  mode: "development" | "test" | "production";
}

export interface AppModule {
  mount: (el: HTMLElement, ctx: AppMountContext) => void | Promise<void>;
  unmount: (el: HTMLElement) => void | Promise<void>;
}

export interface PlatformBus {
  emit<T>(event: string, payload: T): void;
  on<T>(event: string, handler: (p: T) => void): () => void;
}
